// src/services/workload.ts
//
// Deterministic workload / capacity signal. Uses only estimatedMinutes, due
// dates, and calendar events — no AI. Produces an underloaded / balanced /
// overloaded status plus human-readable messages.

import type { Task, Event } from '../types/api';

export type WorkloadStatus = 'underloaded' | 'balanced' | 'overloaded';

export interface WorkloadSummary {
  status: WorkloadStatus;
  /** Estimated minutes of open work due within the horizon. */
  dueSoonMinutes: number;
  dueSoonTaskCount: number;
  /** Due-soon tasks lacking an estimate (not counted in dueSoonMinutes). */
  unestimatedDueSoonCount: number;
  /** Minutes of calendar events today. */
  scheduledMinutesToday: number;
  /** Workday minutes left today after scheduled events. */
  freeMinutesToday: number;
  messages: string[];
}

export interface WorkloadOptions {
  today: string;
  /** Only count tasks assigned to this user or unassigned. Omit for workspace-wide. */
  forUserId?: string | null;
  /** Assumed productive minutes per workday. */
  workdayMinutes?: number;
  /** Days ahead counted as "due soon". */
  horizonDays?: number;
}

const DEFAULT_EVENT_MINUTES = 60;

function hours(minutes: number): string {
  const h = Math.round((minutes / 60) * 10) / 10;
  return `${h}h`;
}

export function computeWorkloadSummary(
  tasks: Task[],
  events: Event[],
  opts: WorkloadOptions,
): WorkloadSummary {
  const { today, forUserId, workdayMinutes = 480, horizonDays = 2 } = opts;
  const horizonStr = (() => {
    const d = new Date(`${today}T00:00:00`);
    d.setDate(d.getDate() + horizonDays);
    return d.toISOString().split('T')[0];
  })();

  // Open work due within the horizon, scoped to the user (or unassigned).
  let dueSoonMinutes = 0;
  let dueSoonTaskCount = 0;
  let unestimatedDueSoonCount = 0;
  for (const t of tasks) {
    if (t.status === 'done') continue;
    if (forUserId && t.assigneeUserId && t.assigneeUserId !== forUserId) continue;
    if (!t.dueDate) continue;
    const due = t.dueDate.split('T')[0];
    if (due < today || due > horizonStr) continue;
    dueSoonTaskCount++;
    if (typeof t.estimatedMinutes === 'number' && t.estimatedMinutes > 0) {
      dueSoonMinutes += t.estimatedMinutes;
    } else {
      unestimatedDueSoonCount++;
    }
  }

  // Calendar load today.
  let scheduledMinutesToday = 0;
  for (const ev of events) {
    if (!ev.startAt || ev.startAt.split('T')[0] !== today) continue;
    if (ev.isAllDay) {
      scheduledMinutesToday += workdayMinutes;
      continue;
    }
    if (ev.endAt) {
      const mins = Math.max(0, (new Date(ev.endAt).getTime() - new Date(ev.startAt).getTime()) / 60000);
      scheduledMinutesToday += Math.round(mins);
    } else {
      scheduledMinutesToday += DEFAULT_EVENT_MINUTES;
    }
  }
  const freeMinutesToday = Math.max(0, workdayMinutes - scheduledMinutesToday);

  // Status (deterministic thresholds).
  const horizonCapacity = workdayMinutes * horizonDays;
  let status: WorkloadStatus;
  if (dueSoonMinutes > horizonCapacity || scheduledMinutesToday >= workdayMinutes) {
    status = 'overloaded';
  } else if (dueSoonMinutes < workdayMinutes * 0.25 && scheduledMinutesToday < workdayMinutes * 0.5) {
    status = 'underloaded';
  } else {
    status = 'balanced';
  }

  const messages: string[] = [];
  if (dueSoonTaskCount > 0) {
    messages.push(`${hours(dueSoonMinutes)} of estimated work due in the next ${horizonDays} days.`);
  }
  messages.push(`${hours(scheduledMinutesToday)} scheduled today with ${hours(freeMinutesToday)} free.`);
  if (unestimatedDueSoonCount > 0) {
    messages.push(`${unestimatedDueSoonCount} due-soon task${unestimatedDueSoonCount > 1 ? 's have' : ' has'} no estimate.`);
  }

  return {
    status,
    dueSoonMinutes,
    dueSoonTaskCount,
    unestimatedDueSoonCount,
    scheduledMinutesToday,
    freeMinutesToday,
    messages,
  };
}
